import { Link } from "react-router-dom";
import { ArrowUpRight, Home, X } from "lucide-react";
import { PillButton } from "@/components/common/ui";
import { BaseGlyph, coords } from "./baseUtils";
import { NearbyHomes } from "./NearbyHomes";

export const EXPLORER_RADII = [5, 10, 15];

const RadiusChips = ({ radius, onRadius }) => (
  <div className="flex gap-1 rounded-full bg-neutral-100 p-1" data-testid="explorer-radius-chips">
    {EXPLORER_RADII.map((r) => (
      <button key={r} type="button" onClick={() => onRadius(r)} data-testid={`explorer-radius-${r}`}
        className={`rounded-full px-3 py-1 text-[12px] transition-colors ${radius === r ? "bg-ink text-white" : "text-neutral-600 hover:text-ink"}`}>
        {r} mi
      </button>
    ))}
  </div>
);

/** Floating detail card for the selected installation: distance rings, closest homes and next steps. */
export const BaseInfoCard = ({ base, radius, onRadius, homes, activeKey, onHover, onPick, onClose, sample }) => (
  <div className="pointer-events-auto w-full animate-rise-in rounded-md bg-white p-4 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.55)] sm:p-6" data-testid="base-info-card">
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <BaseGlyph base={base} selected />
        <div>
          <p className="eyebrow !text-[10px]">{base.branch} · {base.kind === "shipyard" ? "Shipyard" : "Installation"}</p>
          <p className="text-[13px] text-neutral-500">{base.city}, VA · {base.region}</p>
        </div>
      </div>
      <button onClick={onClose} aria-label="Close" data-testid="base-info-close" className="grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100">
        <X className="h-4 w-4" />
      </button>
    </div>
    <h3 className="mt-3 font-display text-2xl font-normal leading-tight text-ink sm:text-[28px]" data-testid="base-info-name">{base.name}</h3>
    <p className="mt-1.5 hidden text-[14px] leading-relaxed text-neutral-600 sm:block">{base.summary}</p>
    <p className="mt-1 hidden font-mono text-[11px] tracking-wide text-neutral-500 sm:block">{coords(base)}</p>

    <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 pt-3">
      <p className="flex items-center gap-1.5 text-[14px] text-ink" data-testid="base-info-homes">
        <Home className="h-4 w-4 text-brass-deep" />
        <strong className="font-medium">{homes.length}</strong> {sample ? "sample " : ""}homes within {radius} mi
      </p>
      <RadiusChips radius={radius} onRadius={onRadius} />
    </div>
    <p className="mb-2 mt-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Closest homes · straight-line distance</p>
    <NearbyHomes homes={homes} activeKey={activeKey} onHover={onHover} onPick={onPick} />

    <div className="mt-4 flex flex-wrap gap-2">
      <PillButton to={`/bases/${base.id}`} testId="base-info-explore">Homes near {base.short}</PillButton>
      <Link to={`/search?base=${base.id}&radius=${radius}&sort=distance`} data-testid="base-info-search"
        className="inline-flex h-11 items-center gap-1.5 rounded-full border border-neutral-300 px-5 text-[15px] text-ink transition-colors hover:border-ink">
        Map search <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  </div>
);
